import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { fetchLargeTrades, fetchPortfolioValue, fetchPositions, fetchPublicProfile } from "@/api/polymarketApi";
import { swrJsonFetcher } from "@/hooks/swr/fetcher";
import { shortAddr } from "../../wallets/lib/format";
import { Chip, Empty, OutcomeTag, SourceError } from "../components/Bits";
import { STATIC_DATA, useLive, useWalletFlags } from "../lib/data";
import { ago, cents, copyText, count, isoDay, pctSigned, stamp, toneClass, usd } from "../lib/format";
import { closeDrawer, openMarket, useParam } from "../lib/nav";
import { useApiStatus } from "../lib/status";
import { findByAddress, llmGuess, personaLabel, type SegmentsDoc, splitPositions, walletDisplayName } from "../lib/wallet";
import { tradeUsd } from "../lib/whales";

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
const POSITION_LIMIT = 25;
const TRADE_LIMIT = 12;
const TRADE_MIN_USD = 1_000;
const UNAVAILABLE = "unavailable";

interface CopyRow {
	address: string;
	copy_5m: number | null;
	copy_5m_ci_lo: number | null;
	copy_5m_ci_hi: number | null;
}

function Section({ title, note, children }: { title: string; note?: string; children: ComponentChildren }) {
	return (
		<section className="border border-line bg-panel">
			<div className="flex flex-wrap items-baseline justify-between gap-x-3 border-b border-line px-2 py-1">
				<h3 className="text-[11px] font-bold text-bright">{title}</h3>
				{note ? <span className="text-[10px] text-mute">{note}</span> : null}
			</div>
			{children}
		</section>
	);
}

function Stat({ label, title, value, tone }: { label: string; title?: string; value: string | null; tone?: string }) {
	return (
		<div className="min-w-0 border-r border-b border-line/60 px-2 py-1" title={title}>
			<div className="text-[9px] uppercase tracking-wide text-mute">{label}</div>
			{value === null ? (
				<div className="truncate text-[11px] leading-[18px] text-mute">{UNAVAILABLE}</div>
			) : (
				<div className={`num truncate text-[12px] font-bold ${tone ?? "text-bright"}`}>{value}</div>
			)}
		</div>
	);
}

const shown = (v: number | null | undefined, format: (n: number) => string): string | null =>
	v === null || v === undefined || !Number.isFinite(v) ? null : format(v);

function Flags({ address }: { address: string }) {
	const api = useApiStatus();
	const flags = useWalletFlags(address);
	const { rows } = flags;
	const empty = !flags.exact
		? `None among the ${count(flags.scanned)} flagged positions the API returned. It cannot search by wallet yet, so older flags are not checked.`
		: flags.emptyReason === "no_flags"
			? "No flags on record for this wallet."
			: "None found so far, but the flag index is still being built, so this is not a clean result.";
	return (
		<Section title="flags" note={flags.total !== null && flags.total > rows.length ? `${rows.length} of ${count(flags.total)} · a flag is a pattern, not proof` : "a flag is a pattern, not proof"}>
			{api.state === "down" || (flags.error && rows.length === 0) ? (
				<Empty>Unavailable: flags come from api.polygains.com, which is not responding.</Empty>
			) : flags.isLoading ? (
				<Empty>loading…</Empty>
			) : rows.length === 0 ? (
				<Empty>{empty}</Empty>
			) : (
				rows.map((r) => (
					<button
						type="button"
						key={r.id}
						disabled={!r.conditionId}
						onClick={() => r.conditionId && openMarket(r.conditionId)}
						className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-2 border-b border-line/50 px-2 py-1 text-left text-[11px] last:border-b-0 hover:bg-raised"
					>
						<span className="truncate text-bright">{r.question}</span>
						<OutcomeTag name={r.outcome} />
						<span className="num font-bold text-bright">{usd(r.volume)}</span>
						<span className="num text-dim">{stamp(r.time)}</span>
					</button>
				))
			)}
		</Section>
	);
}

function Trades({ address }: { address: string }) {
	const { data, error, isLoading, mutate } = useLive(["pm:trades:wallet", address], (signal) =>
		fetchLargeTrades({ minUsd: TRADE_MIN_USD, limit: TRADE_LIMIT, user: address }, signal),
	);
	return (
		<Section title="recent large trades" note={`≥ ${usd(TRADE_MIN_USD)} · Polymarket data-api`}>
			{error && !data ? (
				<SourceError source="Polymarket (data-api)" onRetry={() => void mutate()} />
			) : isLoading && !data ? (
				<Empty>loading…</Empty>
			) : !data?.length ? (
				<Empty>No trades of this size.</Empty>
			) : (
				data.map((t) => (
					<button
						type="button"
						key={`${t.transaction_hash}:${t.token_id}:${t.size}`}
						onClick={() => openMarket(t.condition_id)}
						className="grid w-full grid-cols-[2.25rem_2.5rem_minmax(0,1fr)_auto] items-center gap-2 border-b border-line/50 px-2 py-1 text-left text-[11px] last:border-b-0 hover:bg-raised"
					>
						<span className="num text-dim">{ago(t.timestamp)}</span>
						<Chip tone={t.side === "BUY" ? "up" : "dn"}>{t.side}</Chip>
						<span className="truncate text-bright" title={t.title}>{t.title}</span>
						<span className="num whitespace-nowrap">
							<span className="hidden text-dim sm:inline">{t.outcome.slice(0, 12)} @ {cents(t.price)} </span>
							<span className="font-bold text-bright">{usd(tradeUsd(t))}</span>
						</span>
					</button>
				))
			)}
		</Section>
	);
}

function Positions({ address }: { address: string }) {
	const { data, error, isLoading, mutate } = useLive(["pm:positions", address], (signal) => fetchPositions(address, POSITION_LIMIT, signal));
	const { open: list, spent } = splitPositions(data?.positions ?? []);
	const note = data?.more ? `largest ${list.length} by value, the wallet holds more` : "Polymarket data-api";
	return (
		<Section title="open positions" note={note}>
			{error && !data ? (
				<SourceError source="Polymarket (data-api)" onRetry={() => void mutate()} />
			) : isLoading && !data ? (
				<Empty>loading…</Empty>
			) : list.length === 0 ? (
				<Empty>No open positions with any value{spent ? `; ${spent} resolved at $0 and not yet cleared` : ""}.</Empty>
			) : (
				<>
					<div className="grid grid-cols-[minmax(0,1fr)_3.5rem_4rem] gap-2 border-b border-line px-2 py-0.5 text-right text-[9px] uppercase tracking-wide text-mute sm:grid-cols-[minmax(0,1fr)_6.5rem_3.5rem_4rem]">
						<span className="text-left">market</span>
						<span className="hidden sm:inline">entry → now</span>
						<span>value</span>
						<span title="Open profit or loss on this position, as Polymarket reports it">open P&L</span>
					</div>
					{list.map((p) => (
						<button
							type="button"
							key={`${p.condition_id}:${p.outcome}`}
							onClick={() => openMarket(p.condition_id)}
							className="grid w-full grid-cols-[minmax(0,1fr)_3.5rem_4rem] items-center gap-2 border-b border-line/50 px-2 py-1 text-left text-[11px] last:border-b-0 hover:bg-raised sm:grid-cols-[minmax(0,1fr)_6.5rem_3.5rem_4rem]"
						>
							<span className="flex min-w-0 items-center gap-1.5">
								<span className="max-w-[35%] shrink-0"><OutcomeTag name={p.outcome} /></span>
								<span className="truncate text-bright" title={p.title}>{p.title}</span>
								{p.redeemable ? <Chip title="The market has resolved and this position can be redeemed">redeemable</Chip> : null}
							</span>
							<span className="num hidden text-right text-dim sm:inline">{cents(p.avg_price)} → {cents(p.current_price)}</span>
							<span className="num text-right font-bold text-bright">{usd(p.current_value)}</span>
							<span className={`num text-right ${toneClass(p.unrealized_pnl)}`}>{usd(p.unrealized_pnl)}</span>
						</button>
					))}
				</>
			)}
		</Section>
	);
}

function Panel({ address }: { address: string }) {
	const closeBtn = useRef<HTMLButtonElement>(null);
	const profile = useLive(["pm:profile", address], (signal) => fetchPublicProfile(address, signal));
	const value = useLive(["pm:value", address], (signal) => fetchPortfolioValue(address, signal));
	const { data: segments, error: segmentsError } = useSWR<SegmentsDoc>("data/segments.json", swrJsonFetcher, STATIC_DATA);
	const { data: copy } = useSWR<{ candidates?: CopyRow[] }>("data/copy_candidates.json", swrJsonFetcher, STATIC_DATA);

	const trader = findByAddress(segments?.top_traders, address);
	const copyRow = findByAddress(copy?.candidates, address);
	const name = walletDisplayName(address, trader?.name, profile.data?.name, profile.data?.pseudonym);
	const x = trader?.x_username ?? profile.data?.xUsername ?? null;
	const guess = llmGuess(trader);
	const created = trader?.created_at ?? profile.data?.createdAt ?? null;

	useEffect(() => closeBtn.current?.focus(), [address]);

	const snapshotNote = !segments
		? segmentsError
			? "the segments snapshot did not load, so lifetime figures are unavailable"
			: "loading the segments snapshot…"
		: trader
			? `lifetime and 30-day figures: segments snapshot ${isoDay(segments.generated_at)}`
			: `not among the ${count(segments.top_traders.length)} wallets in the segments snapshot, so lifetime figures are unavailable`;

	return (
		<>
			<div className="shrink-0 border-b border-line px-3 py-2">
				<h2 className="truncate text-[13px] font-bold text-bright" title={address}>{name ?? shortAddr(address)}</h2>
				<div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[11px] text-dim">
					<span className="num flex min-w-0 flex-wrap items-center gap-x-2">
						{name ? <span>{shortAddr(address)}</span> : null}
						{x ? <a href={`https://x.com/${x}`} target="_blank" rel="noreferrer" className="hover:text-up">@{x}</a> : null}
						{profile.data === null ? <span>no Polymarket profile</span> : null}
					</span>
					<CopyAddress address={address} />
					<a href={`https://polymarket.com/profile/${address}`} target="_blank" rel="noreferrer" className="border border-line px-2 py-0.5 text-fg hover:border-up/50">
						polymarket ↗
					</a>
				</div>
			</div>
			<div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
				<div>
					<div className="grid grid-cols-2 border-t border-l border-line/60 bg-panel sm:grid-cols-4">
						<Stat label="Persona (rules)" title="Set by rules over 30 days of behaviour" value={trader?.persona ? personaLabel(trader.persona) : null} />
						<Stat label="Account since" title="Polymarket account creation date" value={created ? isoDay(created) : null} />
						<Stat label="Markets traded" value={shown(trader?.unique_markets, count)} />
						<Stat label="Open value" title="Current value of open positions, from Polymarket" value={shown(value.data, usd)} />
						<Stat label="PnL life" title="Lifetime realised, plus unredeemed positions on resolved markets" value={shown(trader?.pnl_lifetime, usd)} tone={toneClass(trader?.pnl_lifetime)} />
						<Stat label="ROI life" value={shown(trader?.roi, (v) => pctSigned(v))} tone={toneClass(trader?.roi)} />
						<Stat label="Vol 30d" value={shown(trader?.volume_30d, usd)} />
						<Stat label="Open PnL" title="At the snapshot date, marked to the latest price" value={shown(trader?.unrealized_pnl, usd)} tone={toneClass(trader?.unrealized_pnl)} />
					</div>
					<p className="mt-1 text-[10px] leading-4 text-mute">
						{snapshotNote}
						{guess ? <span title={trader?.llm_evidence ?? undefined}> · LLM guess: {guess}. Not used for flags.</span> : null}
						{copyRow ? ` · copy backtest, 5 min late: low ${pctSigned(copyRow.copy_5m_ci_lo, 0)}, mid ${pctSigned(copyRow.copy_5m, 0)}, high ${pctSigned(copyRow.copy_5m_ci_hi, 0)} at 90%, before fees and price impact` : ""}
					</p>
				</div>
				<Flags address={address} />
				<Positions address={address} />
				<Trades address={address} />
			</div>
			<button ref={closeBtn} type="button" onClick={closeDrawer} className="absolute top-1.5 right-3 border border-line px-2 py-0.5 text-[11px] text-dim hover:text-bright">
				close <span className="hidden text-mute sm:inline">esc</span>
			</button>
		</>
	);
}

/** Opens over any view from `?w=0x…`, and stays out of the way otherwise. */
function CopyAddress({ address }: { address: string }) {
	const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
	return (
		<button
			type="button"
			onClick={() => void copyText(address).then((done) => setState(done ? "copied" : "failed"))}
			className="border border-line px-2 py-0.5 text-fg hover:border-up/50"
		>
			{state === "copied" ? "copied" : state === "failed" ? "could not copy" : "copy address"}
		</button>
	);
}

export function WalletDrawer() {
	const [address] = useParam("w");
	const open = address !== "";

	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") closeDrawer();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open]);

	if (!open) return null;
	return (
		<>
			<div className="fixed inset-0 z-40 bg-black/70" onClick={closeDrawer} aria-hidden="true" />
			<aside role="dialog" aria-modal="true" aria-label="Wallet" className="drawer-panel fixed inset-y-0 right-0 z-50 flex w-full max-w-[860px] flex-col border-l border-line bg-ink">
				<div className="flex h-9 shrink-0 items-center border-b border-line px-3">
					<span className="text-xs font-bold text-bright"><span className="text-up">$ </span>wallet</span>
				</div>
				{ADDRESS_RE.test(address) ? (
					<Panel key={address} address={address} />
				) : (
					<>
						<Empty>That is not a wallet address.</Empty>
						<button type="button" onClick={closeDrawer} className="absolute top-1.5 right-3 border border-line px-2 py-0.5 text-[11px] text-dim hover:text-bright">close</button>
					</>
				)}
			</aside>
		</>
	);
}
